import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, ChevronRight, HelpCircle } from 'lucide-react';
import { getFAQs } from '../lib/api';
import { Input } from '../components/ui/input';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { useLanguage } from '../lib/LanguageContext';

export default function FAQPage() {
  const [faqs, setFaqs] = useState([]);
  const [filteredFaqs, setFilteredFaqs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const { t, language } = useLanguage();

  // Helper function to get localized FAQ field
  const getLocalizedField = useCallback((faq, field) => {
    const enField = `${field}_en`;
    const deField = `${field}_de`;
    if (language === 'en' && faq[enField]) {
      return faq[enField];
    }
    return faq[deField] || faq[field] || '';
  }, [language]);

  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        const res = await getFAQs();
        setFaqs(res.data);
        setFilteredFaqs(res.data);
      } catch (err) {
        console.error('Error fetching FAQs:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchFAQs();
  }, []);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredFaqs(faqs);
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    const filtered = faqs.filter((faq) => {
      const question = getLocalizedField(faq, 'question').toLowerCase();
      const answer = getLocalizedField(faq, 'answer').toLowerCase();
      return question.includes(searchLower) || answer.includes(searchLower);
    });
    setFilteredFaqs(filtered);
  }, [searchTerm, faqs, language, getLocalizedField]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="faq-page">
      {/* Hero Section */}
      <section className="relative py-24 lg:py-32 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1920&q=80" 
            alt="FAQ"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0A0A0A]/85" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#D4B982]/20 rounded-full mb-6">
            <HelpCircle className="text-[#D4B982]" size={32} />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            {t('faqPage.title1')} <span className="text-[#D4B982]">{t('faqPage.title2')}</span>
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            {t('faqPage.description')}
          </p>
        </div>
      </section>

      {/* Search & FAQ Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          {/* Search */}
          <div className="relative mb-12">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#71717A]" size={20} />
            <Input
              type="text"
              placeholder={t('faqPage.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 py-6 text-lg border-2 border-[#E4E4E7] focus:border-[#D4B982] rounded-sm"
              data-testid="faq-search"
            />
          </div>

          {/* FAQ Accordion */}
          {filteredFaqs.length > 0 ? (
            <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
              {filteredFaqs.map((faq, index) => (
                <AccordionItem
                  key={faq.id}
                  value={faq.id}
                  className="border border-[#E4E4E7] rounded-sm overflow-hidden bg-white hover:border-[#D4B982] transition-colors"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="px-6 py-5 text-left hover:no-underline hover:bg-[#F4F4F5] transition-colors">
                    <span className="text-[#0A0A0A] font-medium pr-4">{getLocalizedField(faq, 'question')}</span>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-5 text-[#71717A] leading-relaxed">
                    {getLocalizedField(faq, 'answer')}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <div className="text-center py-12">
              <p className="text-[#71717A] mb-4">
                {searchTerm 
                  ? t('faqPage.noResults')
                  : t('faqPage.noFaqs')}
              </p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="text-[#D4B982] font-medium hover:underline"
                >
                  {t('faqPage.resetSearch')}
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#F4F4F5]">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-4">
            {t('faqPage.ctaTitle')}
          </h2>
          <p className="text-[#71717A] mb-6 max-w-xl mx-auto">
            {t('faqPage.ctaDescription')}
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center gap-2 bg-[#D4B982] text-[#0A0A0A] px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#C5A059] transition-colors btn-ripple"
            data-testid="faq-contact-cta"
          >
            {t('faqPage.ctaButton')}
            <ChevronRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
